import dayjs from "dayjs";
import { components } from "../types/api";

export type BriefingHistoryEntry =
  components["schemas"]["BriefingHistoryResponse"];

export type BriefingHistoryChange = { old?: unknown; new?: unknown };

type Translate = (key: string, fallback: string) => string;

/**
 * Qué se hizo en cada entrada del historial. El backend apunta tanto los cambios del
 * briefing como los de sus líneas (goal, target, assets, enlaces...), y una versión nueva
 * trae también el historial de las anteriores.
 */
export const HISTORY_ACTION_LABELS: Record<string, string> = {
  CREATE: "Briefing creado",
  UPDATE: "Datos del briefing modificados",
  STATUS_CHANGE: "Cambio de estado",
  DUPLICATE: "Briefing duplicado",
  VERSION_CREATE: "Nueva versión creada",
  VERSION_SUPERSEDED: "Versión archivada por una nueva",
  LINE_CREATE: "Línea añadida",
  LINE_UPDATE: "Línea modificada",
  LINE_DELETE: "Línea eliminada",
  LINE_DUPLICATE: "Línea duplicada",
  LINE_STATUS_CHANGE: "Estado de la línea cambiado",
  GOAL_CREATE: "Goal creado",
  GOAL_UPDATE: "Goal modificado",
  GOAL_DELETE: "Goal eliminado",
  TARGET_CREATE: "Target creado",
  TARGET_UPDATE: "Target modificado",
  TARGET_DELETE: "Target eliminado",
  ASSET_UPDATE: "Assets modificados",
  SYNC_UPDATE: "Sincronización modificada",
  LINE_PLATFORM_CREATE: "Campaña creada en plataforma",
  LINE_LINK: "Línea enlazada a una campaña",
  LINE_UNLINK: "Línea desenlazada",
  LINE_SYNC_FIX: "Sincronización reparada",
  MARGIN_UPDATE: "Margen modificado",
  DIRECT_BUY_METRICS_UPDATE: "Métricas de compra directa",
};

export type HistoryActionTone =
  | "success"
  | "error"
  | "warning"
  | "info"
  | "primary"
  | "secondary";

export function historyActionTone(action: string): HistoryActionTone {
  if (action.startsWith("VERSION_") || action === "DUPLICATE") return "primary";
  if (action.endsWith("_DELETE") || action === "LINE_UNLINK") return "error";
  if (action.endsWith("CREATE") || action === "LINE_DUPLICATE")
    return "success";
  if (action.includes("STATUS")) return "secondary";
  if (action.includes("LINK") || action.includes("SYNC")) return "info";
  return "warning";
}

export function historyActionLabel(action: string, t: Translate): string {
  return t(
    `briefingsView.history.actions.${action}`,
    HISTORY_ACTION_LABELS[action] ?? action
  );
}

export const HISTORY_FIELD_LABELS: Record<string, string> = {
  // Briefing
  name: "Nombre",
  briefingType: "Tipo de briefing",
  campaignName: "Campaña",
  brandOrProduct: "Marca / producto",
  month: "Mes",
  year: "Año",
  category: "Categoría",
  totalBudget: "Presupuesto total",
  fee: "Fee",
  utm: "UTM",
  trackingPixel: "Píxel de seguimiento",
  status: "Estado",
  hasTags: "Tiene tags",
  notes: "Notas",
  imageUrl: "Imagen",
  company: "Empresa",
  version: "Versión",
  // Línea
  subcampaignName: "Subcampaña",
  sourceId: "ID en plataforma",
  briefingCampaignType: "Tipo de campaña",
  campaignFormat: "Formato",
  visibleToClients: "Visible para el cliente",
  platform: "Plataforma",
  nativeAdProvider: "Proveedor nativo",
  investmentPayment: "Paga la inversión",
  campaignLevel: "Nivel",
  advertiserId: "ID de anunciante",
  internalCampaignId: "Campaña interna (ID)",
  // Goal
  startDate: "Fecha de inicio",
  endDate: "Fecha de fin",
  currency: "Moneda",
  objective: "Objetivo",
  country: "País",
  funnel: "Funnel",
  campaignConcept: "Concepto",
  campaign: "Campaña",
  orderName: "Order name",
  lineName: "Line name",
  expectedVolume: "Volumen esperado",
  visibility: "Visibilidad",
  ctr: "CTR",
  netUnitCost: "Coste unitario neto",
  zeotapDataCost: "Coste de datos Zeotap",
  adserverCost: "Coste de adserver",
  impressionTrackerCost: "Coste tracker de impresiones",
  clickTrackerCost: "Coste tracker de clics",
  adtechCost: "Coste adtech",
  grossUnitCost: "Coste unitario bruto",
  platformInversion: "Inversión plataforma",
  realPlatformInversion: "Inversión plataforma real",
  margin: "Margen",
  totalInversion: "Inversión total",
  realImpressions: "Impresiones reales",
  realClicks: "Clics reales",
  realCompleteViews: "Visualizaciones completas reales",
  realConversions: "Conversiones reales",
  realUniqueUsers: "Usuarios únicos reales",
  manualMetricsAsOf: "Métricas a fecha de",
  // Target
  sex: "Sexo",
  minimumAge: "Edad mínima",
  maximumAge: "Edad máxima",
  language: "Idioma",
  geography: "Geografía",
  technology: "Tecnología",
  interests: "Intereses",
  dataAudience: "Audiencia de datos",
  exclusions: "Exclusiones",
  // Assets
  assetsLinks: "Enlaces de assets",
  copiesLinks: "Enlaces de copies",
  additionalLinks: "Enlaces adicionales",
  destinations: "Destinos",
  destinationUrl: "URL de destino",
  isAutoUtm: "UTM automática",
  // Sync
  beapoloCampaignId: "Campaña BEAPOLO (ID)",
  insertionOrderId: "Insertion order (ID)",
  lineItemId: "Line item (ID)",
  isLinked: "Enlazada",
  // Contexto de la acción
  duplicatedFrom: "Copia de",
  duplicatedTo: "Copiado en",
  newVersion: "Nueva versión",
  lines: "Líneas",
  automatic: "Automático",
  effectiveDate: "Fecha efectiva",
  beapoloCampaign: "Campaña creada",
  group: "Creada en grupo con",
  internalCampaign: "Campaña enlazada",
  idealMarginAccepted: "Margen ideal aceptado",
  description: "Descripción",
};

export function historyFieldLabel(field: string, t: Translate): string {
  return t(
    `briefingsView.history.fields.${field}`,
    HISTORY_FIELD_LABELS[field] ?? field
  );
}

const PERCENT_FIELDS = new Set(["margin", "fee", "ctr"]);
const MONEY_FIELDS = new Set([
  "totalBudget",
  "netUnitCost",
  "zeotapDataCost",
  "adserverCost",
  "impressionTrackerCost",
  "clickTrackerCost",
  "adtechCost",
  "grossUnitCost",
  "platformInversion",
  "realPlatformInversion",
  "totalInversion",
]);
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+Z?)?$/;

export function isEmptyHistoryValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

/** Un valor del diff en texto legible: fechas, importes, porcentajes, listas y enlaces. */
export function formatHistoryValue(
  field: string,
  value: unknown,
  t: Translate,
  locale = "es"
): string {
  if (isEmptyHistoryValue(value)) return "—";
  if (typeof value === "boolean")
    return value ? t("common.yes", "Sí") : t("common.no", "No");
  if (typeof value === "number") {
    const formatted = value.toLocaleString(locale, {
      maximumFractionDigits: MONEY_FIELDS.has(field) ? 2 : 4,
      minimumFractionDigits: MONEY_FIELDS.has(field) ? 2 : 0,
    });
    return PERCENT_FIELDS.has(field) ? `${formatted} %` : formatted;
  }
  if (typeof value === "string") {
    if (ISO_DATE.test(value) && dayjs(value).isValid()) {
      return dayjs(value).format("DD/MM/YYYY");
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        item && typeof item === "object"
          ? String(
              (item as { url?: string; name?: string }).url ??
                (item as { name?: string }).name ??
                JSON.stringify(item)
            )
          : formatHistoryValue(field, item, t, locale)
      )
      .join(", ");
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => !isEmptyHistoryValue(v)
    );
    if (!entries.length) return "—";
    return entries
      .map(
        ([key, v]) =>
          `${key}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`
      )
      .join(" · ");
  }
  return String(value);
}

/** Los cambios de una entrada que de verdad dicen algo (sin campos que no cambiaron). */
export function historyChangeRows(
  changes: unknown
): Array<[string, BriefingHistoryChange]> {
  if (!changes || typeof changes !== "object" || Array.isArray(changes))
    return [];
  return Object.entries(
    changes as Record<string, BriefingHistoryChange>
  ).filter(
    ([field, change]) =>
      field !== "updatedAt" &&
      change &&
      typeof change === "object" &&
      !(isEmptyHistoryValue(change.old) && isEmptyHistoryValue(change.new))
  );
}

export function historyUserName(
  user: BriefingHistoryEntry["user"],
  fallback: string
): string {
  if (!user) return fallback;
  return `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email;
}

/** Si el campo "status" de esta entrada es el del briefing o el de una línea. */
export function isLineEntry(entry: BriefingHistoryEntry): boolean {
  return entry.entityType === "LINE";
}
